import LaborMonthlyEditor from './LaborMonthlyEditor';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Check, Trash2 } from 'lucide-react';
import { deleteLaborBudget } from '../../utils/laborBudgetApi';
import { useOptimisticNotifications } from '../../hooks/useOptimisticNotifications';
import { InlineSelect, InlineInput, InlineCheckbox } from '../../components/InlineTable';

const DRIVER_OPTIONS = [
  { value: 'FTE', label: 'FTE' },
  { value: 'HPOR', label: 'Hours / occupied room' },
  { value: 'MIN_POR', label: 'Minutes / occupied room' },
  { value: 'PCT_REVENUE', label: '% of Revenue' },
];

// Small inline spinner component
const MiniSpinner = () => (
  <svg className="animate-spin h-4 w-4 text-gray-400 mx-auto" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// One labor-driver row — inline driver select, numeric input, weekend checkbox.
// Hover state drives rowActive so controls surface cleanly without column reflow.
const DriverRow = ({ row, unsaved, onFieldChange, onDelete, onMonthly }) => {
  const [hov, setHov] = useState(false);
  return (
    <tr
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ background: unsaved ? 'rgba(255,122,0,.05)' : hov ? 'rgba(255,122,0,.03)' : 'var(--lodge-white)', transition: 'background .1s' }}
    >
      <td style={{ padding: '0 12px', fontSize: 13, color: 'var(--lodge-onyx-700)', whiteSpace: 'nowrap' }}>{row.departmentName || '—'}</td>
      <td style={{ padding: '0 12px', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--lodge-onyx-500)', whiteSpace: 'nowrap' }}>{row.code}</td>
      <td style={{ padding: '0 12px', fontSize: 13, color: 'var(--lodge-onyx-900)', whiteSpace: 'nowrap' }}>{row.name}</td>
      <td style={{ width: 150 }}>
        <InlineSelect
          value={row.driverType || null}
          options={DRIVER_OPTIONS}
          onChange={(v) => onFieldChange('driverType', v || '')}
          placeholder="Driver…"
          rowActive={hov}
          ariaLabel="Budget driver type"
        />
      </td>
      <td style={{ width: 110 }}>
        <InlineInput
          type="number"
          value={row.driverInput}
          onChange={(v) => onFieldChange('driverInput', v)}
          placeholder="0.00"
          rowActive={hov}
          mono
          debounceMs={0}
          step={row.driverType === 'PCT_REVENUE' ? '0.01' : '0.1'}
          suffix={row.driverType === 'PCT_REVENUE' ? '%' : undefined}
        />
      </td>
      <td style={{ width: 110 }}><InlineInput type="number" value={row.hourlyRate} onChange={v => onFieldChange('hourlyRate', v)} placeholder="Payroll rate" rowActive={hov} debounceMs={0} step="0.01" /></td>
      <td><button className="text-orange-700 text-sm whitespace-nowrap px-2" onClick={() => onMonthly(row)}>Monthly budgets</button></td>
      <td style={{ width: 110, textAlign: 'center' }}>
        <InlineCheckbox checked={row.worksWeekend} onChange={() => onFieldChange('worksWeekend', !row.worksWeekend)} title="Works weekend" />
      </td>
      <td style={{ width: 56, textAlign: 'center' }}>
        {unsaved ? <MiniSpinner /> : <Check className="mx-auto" size={16} style={{ color: 'var(--color-success)' }} />}
      </td>
      <td style={{ width: 48, textAlign: 'center' }}>
        {row.budgetId && (
          <button onClick={() => onDelete(row)} style={{ color: 'var(--color-error)', background: 'transparent', border: 'none', cursor: 'pointer', display: 'inline-flex' }} title="Delete budget entry">
            <Trash2 size={14} />
          </button>
        )}
      </td>
    </tr>
  );
};

// Props:
// positions: array of positionAccounts (code, name, id, departmentId)
// departments: array of departments (id, name)
// budgetData: array from API (existing labor budgets)
// location, orgId
// searchTerm: filter string for positions
// api: { createBudget, updateBudget }
const LaborDriverTable = ({
  positions = [],
  departments = [],
  budgetData = [],
  location,
  orgId,
  searchTerm = '',
  year = new Date().getFullYear(),
  api = {}
}) => {
  const { createBudget, updateBudget } = api;
  const [monthlyRow, setMonthlyRow] = useState(null);
  const notify = useOptimisticNotifications();

  // Create a map for quick department lookup
  const departmentMap = useMemo(() => {
    const map = {};
    departments.forEach(dept => {
      map[dept.id] = dept.name;
    });
    return map;
  }, [departments]);

  // Build initial rows combining positions and existing data
  const buildRows = () => {
    const budgetMap = {};
    budgetData.forEach((b) => {
      if (b && b.positionId !== undefined) {
        budgetMap[b.positionId] = b;
      }
    });

    return positions.map((pos) => {
      const existing = budgetMap[pos.id] || {};
      return {
        budgetId: existing.id || null,
        positionId: pos.id,
        code: pos.accountNumber,
        name: pos.accountName,
        departmentId: pos.departmentId,
        departmentName: departmentMap[pos.departmentId] || '',
        driverType: existing.budgetDriverType || '',
        driverInput: existing.budgetDriverInput ?? '',
        hourlyRate: existing.budgetHourlyRate ?? '',
        worksWeekend: existing.worksWeekend === true || existing.worksWeekend === 1,
      };
    });
  };

  const [rows, setRows] = useState(buildRows);
  const [unsavedRows, setUnsavedRows] = useState(new Set());
  const saveTimersRef = useRef({});

  // Warn user before leaving (full reload) if unsaved, but internal SPA navigation auto-saves so no custom blocking now.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedRows.size > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedRows]);

  // Rebuild rows when inputs change
  useEffect(() => {
    setRows(buildRows());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, budgetData, departmentMap]);

  const handleFieldChange = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
    setUnsavedRows(prev => new Set(prev).add(updated[index].positionId));

    // Debounced auto-save for this row
    const pid = updated[index].positionId;
    if (saveTimersRef.current[pid]) {
      clearTimeout(saveTimersRef.current[pid]);
    }
    saveTimersRef.current[pid] = setTimeout(() => {
      saveRow(updated[index]);
      delete saveTimersRef.current[pid];
    }, 1500); // 1.5s debounce
  };

  const saveRow = async (row) => {
    const payload = {
      id: row.budgetId,
      positionId: row.positionId,
      orgId: orgId,
      locationId: location.id,
      budgetDriverType: row.driverType,
      budgetDriverInput: row.driverInput,
      worksWeekend: row.worksWeekend,
      budgetHourlyRate: row.hourlyRate === '' ? null : Number(row.hourlyRate),
    };

    // Store original state for potential revert
    const originalRows = rows;
    const originalUnsavedRows = new Set(unsavedRows);

    // Optimistically mark as saved
    setUnsavedRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(row.positionId);
      return newSet;
    });

    try {
      const isUpdate = Boolean(row.budgetId);
      const operation = isUpdate ? updateBudget(payload) : createBudget(payload);

      await notify.success(
        `${row.code} saved`,
        operation,
        `Error saving ${row.code}`,
        {
          onSuccess: (result) => {
            // Update local row with returned ID and any new fields
            if (result && result.id) {
              setRows(prevRows => prevRows.map(r => (
                r.positionId === row.positionId ? {
                  ...r,
                  budgetId: result.id,
                  driverType: result.budgetDriverType ?? r.driverType,
                  driverInput: result.budgetDriverInput ?? r.driverInput,
                  worksWeekend: result.worksWeekend === true || result.worksWeekend === 1,
                } : r
              )));
            }

          },
          revertAction: () => {
            setRows(originalRows);
            setUnsavedRows(originalUnsavedRows);
          }
        }
      );
    } catch (err) {
      console.error('Error saving labor budget row', err);
      // Error handling already done by notify.success
    }
  };

  // Filter rows based on search term
  const filteredRows = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return rows;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return rows.filter(row => {
      return (
        row.code?.toLowerCase().includes(lowerSearchTerm) ||
        row.name?.toLowerCase().includes(lowerSearchTerm) ||
        row.departmentName?.toLowerCase().includes(lowerSearchTerm)
      );
    });
  }, [rows, searchTerm]);

  // Delete a saved budget entry (optimistic, with revert on failure)
  const handleDeleteRow = async (row) => {
    if (!window.confirm('Delete this budget entry?')) return;

    const originalRow = { ...row };
    setRows(prev => prev.map(r => (
      r.positionId === row.positionId ? { ...r, budgetId: null, driverInput: '', driverType: '', worksWeekend: false } : r
    )));

    try {
      await notify.success(
        'Deleted',
        deleteLaborBudget(row.budgetId),
        'Delete failed',
        {
          revertAction: () => {
            setRows(prev => prev.map(r => (
              r.positionId === row.positionId ? originalRow : r
            )));
          }
        }
      );
    } catch (err) {
      // Error handling already done by notify.success
    }
  };

  return (
    <div className="it-scroll">
      <table className="it-tbl" style={{ minWidth: 860 }}>
        <thead>
          <tr style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <th style={{ width: 190 }}>Department</th>
            <th style={{ width: 110 }}>Code</th>
            <th style={{ width: 200 }}>Position</th>
            <th style={{ width: 150 }}>Driver Type</th>
            <th style={{ width: 110 }}>Driver Input</th>
            <th style={{ width: 110 }}>Hourly rate</th>
            <th style={{ width: 130 }}>Monthly</th>
            <th style={{ width: 110, textAlign: 'center' }}>Weekend</th>
            <th style={{ width: 56, textAlign: 'center' }}>Status</th>
            <th style={{ width: 48, textAlign: 'center' }}></th>
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((row) => {
            // Find the original index in the full rows array for field updates
            const originalIdx = rows.findIndex(r => r.positionId === row.positionId);
            return (
              <DriverRow
                key={row.positionId}
                row={row}
                unsaved={unsavedRows.has(row.positionId)}
                onFieldChange={(field, value) => handleFieldChange(originalIdx, field, value)}
                onDelete={handleDeleteRow}
                onMonthly={setMonthlyRow}
              />
            );
          })}
        </tbody>
      </table>
      {monthlyRow && <LaborMonthlyEditor key={`${location.id}:${monthlyRow.positionId}:${year}`} row={monthlyRow} orgId={orgId} locationId={location.id} year={year} onClose={() => setMonthlyRow(null)} />}
    </div>
  );
};

export default LaborDriverTable;
